import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiArrowLeft, FiSave, FiDownload, FiPrinter, FiRefreshCw, FiEye, 
  FiGrid, FiLayers, FiType, FiImage, FiList, FiBox, FiAlignLeft,
  FiAlignCenter, FiAlignRight, FiPlus, FiMinus, FiTrash2, FiMove,
  FiEdit, FiCheck, FiX, FiFileText, FiColumns, FiLayout, FiTable,
  FiCpu, FiMenu, FiCheckSquare, FiSquare, FiSettings, FiChevronRight,
  FiChevronDown
} = FiIcons;

// Default template structures
const DEFAULT_COVER_TEMPLATE = {
  name: 'Cover Page Template',
  type: 'visual-builder',
  templateType: 'cover',
  sections: [
    {
      id: 'header',
      type: 'header',
      content: {
        logo: 'https://i.postimg.cc/ryrbYs4h/Logo-UPNM.png',
        institution: 'NATIONAL DEFENCE UNIVERSITY OF MALAYSIA',
        location: 'SUNGAI BESI CAMP',
        address: '57000 KUALA LUMPUR'
      },
      style: {
        alignment: 'center',
        spacing: 'normal',
        divider: true
      }
    },
    {
      id: 'exam-title',
      type: 'title',
      content: {
        examType: 'FINAL EXAMINATION',
        programLevel: 'DEGREE PROGRAMME',
        semesterSession: 'SEMESTER II ACADEMIC SESSION 2024/2025'
      },
      style: {
        alignment: 'center',
        spacing: 'large',
        divider: false
      }
    },
    {
      id: 'course-info',
      type: 'courseInfo',
      content: {
        courseTitle: 'ENGLISH FOR ACADEMIC WRITING',
        courseCode: 'LLE 3012',
        duration: '2 HOURS',
        programmeCode: 'ALL ACADEMIC PROGRAMMES'
      },
      style: {
        alignment: 'left',
        indented: true,
        labelWidth: '160px'
      }
    },
    {
      id: 'instructions',
      type: 'instructions',
      content: {
        title: 'INSTRUCTIONS :',
        items: [
          'ANSWER ALL QUESTIONS IN SECTION A, B AND C.',
          'ANSWER ALL QUESTIONS IN THE QUESTION PAGE.'
        ]
      },
      style: {
        alignment: 'left',
        indented: true,
        spacing: 'normal'
      }
    },
    {
      id: 'footer',
      type: 'footer',
      content: {
        pageCount: 'TWELVE (12)'
      },
      style: {
        divider: true,
        alignment: 'center'
      }
    }
  ],
  settings: {
    pageSize: 'A4',
    orientation: 'portrait',
    margins: {
      top: '1in',
      right: '1in',
      bottom: '1in',
      left: '1in'
    },
    font: {
      family: 'Arial, serif',
      size: '12pt',
      lineHeight: '2'
    }
  }
};

const DEFAULT_BODY_TEMPLATE = {
  name: 'Body Page Template',
  type: 'visual-builder',
  templateType: 'body',
  sections: [
    {
      id: 'header',
      type: 'pageHeader',
      content: {
        confidential: 'CONFIDENTIAL'
      },
      style: {
        alignment: 'right',
        bold: true
      }
    },
    {
      id: 'clo-table',
      type: 'cloTable',
      content: {
        title: 'COURSE LEARNING OUTCOMES',
        clos: [
          { number: 'CLO1', description: 'Produce detailed text with accurate techniques of paraphrasing (C3).', plo: 'PLO1' },
          { number: 'CLO2', description: 'Synthesise information and arguments from a number of sources (C2).', plo: 'PLO1' },
          { number: 'CLO3', description: 'Produce synthesised essays which develops an argument, giving reasons in support of or against a particular point of view (C3).', plo: 'PLO2' }
        ]
      },
      style: {
        alignment: 'center',
        tableStyle: 'bordered'
      }
    },
    {
      id: 'section-a',
      type: 'examSection',
      content: {
        title: 'SECTION A',
        instruction: 'ANSWER ALL QUESTIONS',
        subsections: [
          {
            title: 'USING SYNONYMS (10 MARKS)',
            questions: [
              {
                type: 'multiple-choice',
                text: 'Select the best synonym for the underlined word.',
                subQuestions: [
                  {
                    text: 'The <u>outcome</u> of the research was surprising.',
                    options: ['a) method', 'b) result', 'c) purpose', 'd) hypothesis'],
                    cloMapping: 'CLO1:PLO1 - 2 marks'
                  },
                  {
                    text: 'The professor <u>recommended</u> additional readings.',
                    options: ['a) assigned', 'b) suggested', 'c) ordered', 'd) noted'],
                    cloMapping: 'CLO1:PLO1 - 2 marks'
                  }
                ]
              }
            ]
          },
          {
            title: 'PASSIVE-ACTIVE VOICE (10 MARKS)',
            instruction: 'Instructions: Rewrite the following sentences by changing the voice from passive to active or vice versa, as directed.',
            questions: [
              {
                type: 'short-answer',
                subQuestions: [
                  {
                    text: 'The report was written by the student. (Convert to active voice)',
                    responseLines: 3,
                    cloMapping: 'CLO1:PLO1 - 2 marks'
                  },
                  {
                    text: 'Researchers conducted the survey last month. (Convert to passive voice)',
                    responseLines: 3,
                    cloMapping: 'CLO1:PLO1 - 2 marks'
                  }
                ]
              }
            ]
          }
        ]
      },
      style: {
        sectionNumbering: 'alphabet',
        questionNumbering: 'decimal',
        subQuestionNumbering: 'roman'
      }
    },
    {
      id: 'section-b',
      type: 'examSection',
      content: {
        title: 'SECTION B',
        instruction: 'ANSWER ALL QUESTIONS',
        subsections: [
          {
            title: 'PARAPHRASING SKILLS (20 MARKS)',
            instruction: 'Instructions: Paraphrase the following paragraphs using a combination of techniques such as synonyms, passive-active voice changes, and sentence restructuring. Cite the text correctly using APA style in-text citations.',
            questions: [
              {
                type: 'essay',
                subQuestions: [
                  {
                    text: '"Educational technology has revolutionized the learning environment, providing more interactive and student-centred approaches. This transformation allows students to engage actively in the learning process, enhancing their critical thinking and problem-solving skills" (Anderson & Johnson, 2018).',
                    responseLines: 10,
                    cloMapping: 'CLO1:PLO1 - 10 marks'
                  }
                ]
              }
            ]
          }
        ]
      },
      style: {
        sectionNumbering: 'alphabet',
        questionNumbering: 'decimal',
        subQuestionNumbering: 'roman'
      }
    },
    {
      id: 'footer',
      type: 'pageFooter',
      content: {
        left: 'Final Exam . Sem . II , Session 2024 / 2025 (S1)',
        center: '{page} of {total}',
        right: 'LLE 3012'
      },
      style: {
        fontSize: '10pt'
      }
    }
  ],
  settings: {
    pageSize: 'A4',
    orientation: 'portrait',
    margins: {
      top: '0.58in',
      right: '1.18in',
      bottom: '0.58in',
      left: '1.18in'
    },
    font: {
      family: 'Arial, sans-serif',
      size: '12pt',
      lineHeight: '1.15'
    },
    pageNumbering: {
      start: 3,
      format: '{page} of {total}'
    }
  }
};

// Available component types for each template type
const COVER_COMPONENTS = [
  { id: 'header', label: 'Institution Header', icon: FiMenu },
  { id: 'title', label: 'Exam Title', icon: FiType },
  { id: 'courseInfo', label: 'Course Information', icon: FiFileText },
  { id: 'instructions', label: 'Instructions', icon: FiList },
  { id: 'footer', label: 'Footer Note', icon: FiLayout }
];

const BODY_COMPONENTS = [
  { id: 'pageHeader', label: 'Page Header', icon: FiMenu },
  { id: 'cloTable', label: 'CLO Table', icon: FiTable },
  { id: 'examSection', label: 'Exam Section', icon: FiLayout },
  { id: 'subsection', label: 'Subsection', icon: FiList },
  { id: 'question', label: 'Question', icon: FiFileText },
  { id: 'pageFooter', label: 'Page Footer', icon: FiLayout }
];

// Question types
const QUESTION_TYPES = [
  { id: 'multiple-choice', label: 'Multiple Choice', icon: FiCheckSquare },
  { id: 'short-answer', label: 'Short Answer', icon: FiEdit },
  { id: 'essay', label: 'Essay', icon: FiFileText },
  { id: 'fill-blank', label: 'Fill in Blank', icon: FiType }
];

const TemplateEditor = ({ onBack, template = null }) => {
  // Initialize with template or default
  const [templateData, setTemplateData] = useState(() => {
    if (template) {
      return template;
    }
    return {
      ...DEFAULT_BODY_TEMPLATE,
      id: Date.now().toString(),
      name: 'New Template',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  });

  const [templateType, setTemplateType] = useState(template?.templateType || 'body');
  const [activeTab, setActiveTab] = useState('builder');
  const [showPreview, setShowPreview] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [editingText, setEditingText] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedSettings, setExpandedSettings] = useState({
    page: false,
    font: false,
    header: false,
    footer: false,
    numbering: false
  });
  const [showComponentPanel, setShowComponentPanel] = useState(true);
  const [generatedHTML, setGeneratedHTML] = useState('');
  const previewRef = useRef(null);

  // Change template type
  useEffect(() => {
    if (templateType === 'cover' && !template) {
      setTemplateData({
        ...DEFAULT_COVER_TEMPLATE,
        id: Date.now().toString(),
        name: 'New Cover Template',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } else if (templateType === 'body' && !template) {
      setTemplateData({
        ...DEFAULT_BODY_TEMPLATE,
        id: Date.now().toString(),
        name: 'New Body Template',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
  }, [templateType, template]);

  // Generate HTML whenever template data changes
  useEffect(() => {
    generateHTML();
  }, [templateData]);

  // Toggle settings sections
  const toggleSettingsSection = (section) => {
    setExpandedSettings(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle drag start
  const handleDragStart = (e, item, type) => {
    setIsDragging(true);
    setDraggedItem({ ...item, type });
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  // Handle drop
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setIsDragging(false);
    setDragOverIndex(null);

    if (draggedItem.type === 'new') {
      // Add new component
      const newSection = createNewSection(draggedItem.id);
      if (newSection) {
        const updatedSections = [...templateData.sections];
        updatedSections.splice(dropIndex, 0, newSection);
        setTemplateData({
          ...templateData,
          sections: updatedSections
        });
        setSelectedSectionIndex(dropIndex);
      }
    } else if (draggedItem.type === 'existing') {
      // Reorder existing component
      const updatedSections = [...templateData.sections];
      const [movedItem] = updatedSections.splice(draggedItem.index, 1);
      updatedSections.splice(dropIndex, 0, movedItem);
      setTemplateData({
        ...templateData,
        sections: updatedSections
      });
      setSelectedSectionIndex(dropIndex);
    }
    
    setDraggedItem(null);
  };

  // Create a new section based on component type
  const createNewSection = (type) => {
    switch (type) {
      case 'header':
        return {
          id: `header-${Date.now()}`,
          type: 'header',
          content: {
            logo: 'https://i.postimg.cc/ryrbYs4h/Logo-UPNM.png',
            institution: 'NATIONAL DEFENCE UNIVERSITY OF MALAYSIA',
            location: 'SUNGAI BESI CAMP',
            address: '57000 KUALA LUMPUR'
          },
          style: {
            alignment: 'center',
            spacing: 'normal',
            divider: true
          }
        };
      case 'title':
        return {
          id: `title-${Date.now()}`,
          type: 'title',
          content: {
            examType: 'FINAL EXAMINATION',
            programLevel: 'DEGREE PROGRAMME',
            semesterSession: 'SEMESTER II ACADEMIC SESSION 2024/2025'
          },
          style: {
            alignment: 'center',
            spacing: 'large',
            divider: false
          }
        };
      case 'courseInfo':
        return {
          id: `course-info-${Date.now()}`,
          type: 'courseInfo',
          content: {
            courseTitle: 'ENGLISH FOR ACADEMIC WRITING',
            courseCode: 'LLE 3012',
            duration: '2 HOURS',
            programmeCode: 'ALL ACADEMIC PROGRAMMES'
          },
          style: {
            alignment: 'left',
            indented: true,
            labelWidth: '160px'
          }
        };
      case 'instructions':
        return {
          id: `instructions-${Date.now()}`,
          type: 'instructions',
          content: {
            title: 'INSTRUCTIONS :',
            items: [
              'ANSWER ALL QUESTIONS IN SECTION A, B AND C.',
              'ANSWER ALL QUESTIONS IN THE QUESTION PAGE.'
            ]
          },
          style: {
            alignment: 'left',
            indented: true,
            spacing: 'normal'
          }
        };
      case 'footer':
        return {
          id: `footer-${Date.now()}`,
          type: 'footer',
          content: {
            pageCount: 'TWELVE (12)'
          },
          style: {
            divider: true,
            alignment: 'center'
          }
        };
      case 'pageHeader':
        return {
          id: `page-header-${Date.now()}`,
          type: 'pageHeader',
          content: {
            confidential: 'CONFIDENTIAL'
          },
          style: {
            alignment: 'right',
            bold: true
          }
        };
      case 'cloTable':
        return {
          id: `clo-table-${Date.now()}`,
          type: 'cloTable',
          content: {
            title: 'COURSE LEARNING OUTCOMES',
            clos: [
              { number: 'CLO1', description: 'Produce detailed text with accurate techniques of paraphrasing (C3).', plo: 'PLO1' },
              { number: 'CLO2', description: 'Synthesise information and arguments from a number of sources (C2).', plo: 'PLO1' }
            ]
          },
          style: {
            alignment: 'center',
            tableStyle: 'bordered'
          }
        };
      case 'examSection':
        return {
          id: `section-${Date.now()}`,
          type: 'examSection',
          content: {
            title: 'SECTION A',
            instruction: 'ANSWER ALL QUESTIONS',
            subsections: [
              {
                title: 'USING SYNONYMS (10 MARKS)',
                questions: [
                  {
                    type: 'multiple-choice',
                    text: 'Select the best synonym for the underlined word.',
                    subQuestions: [
                      {
                        text: 'The <u>outcome</u> of the research was surprising.',
                        options: ['a) method', 'b) result', 'c) purpose', 'd) hypothesis'],
                        cloMapping: 'CLO1:PLO1 - 2 marks'
                      }
                    ]
                  }
                ]
              }
            ]
          },
          style: {
            sectionNumbering: 'alphabet',
            questionNumbering: 'decimal',
            subQuestionNumbering: 'roman'
          }
        };
      case 'pageFooter':
        return {
          id: `page-footer-${Date.now()}`,
          type: 'pageFooter',
          content: {
            left: 'Final Exam . Sem . II , Session 2024 / 2025 (S1)',
            center: '{page} of {total}',
            right: 'LLE 3012'
          },
          style: {
            fontSize: '10pt'
          }
        };
      default:
        return null;
    }
  };

  // Handle section selection
  const handleSelectSection = (index) => {
    setSelectedSectionIndex(index === selectedSectionIndex ? null : index);
  };

  // Handle section deletion
  const handleDeleteSection = (index) => {
    const updatedSections = [...templateData.sections];
    updatedSections.splice(index, 1);
    setTemplateData({
      ...templateData,
      sections: updatedSections
    });
    setSelectedSectionIndex(null);
  };

  // Handle section update
  const handleUpdateSection = (index, updates) => {
    const updatedSections = [...templateData.sections];
    updatedSections[index] = {
      ...updatedSections[index],
      ...updates
    };
    setTemplateData({
      ...templateData,
      sections: updatedSections
    });
  };

  // Handle content update
  const handleUpdateContent = (index, key, value) => {
    const updatedSections = [...templateData.sections];
    updatedSections[index] = {
      ...updatedSections[index],
      content: {
        ...updatedSections[index].content,
        [key]: value
      }
    };
    setTemplateData({
      ...templateData,
      sections: updatedSections
    });
  };

  // Handle nested content update (for arrays and objects)
  const handleUpdateNestedContent = (index, key, nestedKey, value) => {
    const updatedSections = [...templateData.sections];
    const section = updatedSections[index];
    
    if (Array.isArray(section.content[key])) {
      // Handle array updates
      const updatedArray = [...section.content[key]];
      updatedArray[nestedKey] = value;
      
      updatedSections[index] = {
        ...section,
        content: {
          ...section.content,
          [key]: updatedArray
        }
      };
    } else if (typeof section.content[key] === 'object') {
      // Handle object updates
      updatedSections[index] = {
        ...section,
        content: {
          ...section.content,
          [key]: {
            ...section.content[key],
            [nestedKey]: value
          }
        }
      };
    }
    
    setTemplateData({
      ...templateData,
      sections: updatedSections
    });
  };

  // Handle style update
  const handleUpdateStyle = (index, key, value) => {
    const updatedSections = [...templateData.sections];
    updatedSections[index] = {
      ...updatedSections[index],
      style: {
        ...updatedSections[index].style,
        [key]: value
      }
    };
    setTemplateData({
      ...templateData,
      sections: updatedSections
    });
  };

  // Handle settings update
  const handleUpdateSettings = (key, value) => {
    setTemplateData({
      ...templateData,
      settings: {
        ...templateData.settings,
        [key]: value
      }
    });
  };

  // Handle nested settings update
  const handleUpdateNestedSettings = (parentKey, key, value) => {
    setTemplateData({
      ...templateData,
      settings: {
        ...templateData.settings,
        [parentKey]: {
          ...templateData.settings[parentKey],
          [key]: value
        }
      }
    });
  };

  // Add CLO to table
  const handleAddCLO = (index) => {
    const section = templateData.sections[index];
    if (section.type === 'cloTable') {
      const clos = [...section.content.clos];
      const newCLO = {
        number: `CLO${clos.length + 1}`,
        description: 'New Course Learning Outcome',
        plo: 'PLO1'
      };
      clos.push(newCLO);
      handleUpdateContent(index, 'clos', clos);
    }
  };

  // Delete CLO from table
  const handleDeleteCLO = (sectionIndex, cloIndex) => {
    const section = templateData.sections[sectionIndex];
    if (section.type === 'cloTable') {
      const clos = [...section.content.clos];
      clos.splice(cloIndex, 1);
      handleUpdateContent(sectionIndex, 'clos', clos);
    }
  };

  // Add instruction item
  const handleAddInstructionItem = (index) => {
    const section = templateData.sections[index];
    if (section.type === 'instructions') {
      const items = [...section.content.items];
      items.push('New instruction item');
      handleUpdateContent(index, 'items', items);
    }
  };

  // Delete instruction item
  const handleDeleteInstructionItem = (sectionIndex, itemIndex) => {
    const section = templateData.sections[sectionIndex];
    if (section.type === 'instructions') {
      const items = [...section.content.items];
      items.splice(itemIndex, 1);
      handleUpdateContent(sectionIndex, 'items', items);
    }
  };

  // Add subsection to exam section
  const handleAddSubsection = (index) => {
    const section = templateData.sections[index];
    if (section.type === 'examSection') {
      const subsections = [...section.content.subsections];
      const newSubsection = {
        title: 'New Subsection (10 MARKS)',
        questions: [
          {
            type: 'multiple-choice',
            text: 'New question',
            subQuestions: [
              {
                text: 'New sub-question',
                options: ['a) Option 1', 'b) Option 2', 'c) Option 3', 'd) Option 4'],
                cloMapping: 'CLO1:PLO1 - 2 marks'
              }
            ]
          }
        ]
      };
      subsections.push(newSubsection);
      handleUpdateContent(index, 'subsections', subsections);
    }
  };

  // Delete subsection
  const handleDeleteSubsection = (sectionIndex, subsectionIndex) => {
    const section = templateData.sections[sectionIndex];
    if (section.type === 'examSection') {
      const subsections = [...section.content.subsections];
      subsections.splice(subsectionIndex, 1);
      handleUpdateContent(sectionIndex, 'subsections', subsections);
    }
  };

  // Add question to subsection
  const handleAddQuestion = (sectionIndex, subsectionIndex) => {
    const section = templateData.sections[sectionIndex];
    if (section.type === 'examSection') {
      const subsections = [...section.content.subsections];
      const newQuestion = {
        type: 'multiple-choice',
        text: 'New question',
        subQuestions: [
          {
            text: 'New sub-question',
            options: ['a) Option 1', 'b) Option 2', 'c) Option 3', 'd) Option 4'],
            cloMapping: 'CLO1:PLO1 - 2 marks'
          }
        ]
      };
      subsections[subsectionIndex].questions.push(newQuestion);
      handleUpdateContent(sectionIndex, 'subsections', subsections);
    }
  };

  // Delete question
  const handleDeleteQuestion = (sectionIndex, subsectionIndex, questionIndex) => {
    const section = templateData.sections[sectionIndex];
    if (section.type === 'examSection') {
      const subsections = [...section.content.subsections];
      subsections[subsectionIndex].questions.splice(questionIndex, 1);
      handleUpdateContent(sectionIndex, 'subsections', subsections);
    }
  };

  // Add sub-question
  const handleAddSubQuestion = (sectionIndex, subsectionIndex, questionIndex) => {
    const section = templateData.sections[sectionIndex];
    if (section.type === 'examSection') {
      const subsections = [...section.content.subsections];
      const question = subsections[subsectionIndex].questions[questionIndex];
      const subQuestions = [...question.subQuestions];
      
      let newSubQuestion = {};
      if (question.type === 'multiple-choice') {
        newSubQuestion = {
          text: 'New sub-question',
          options: ['a) Option 1', 'b) Option 2', 'c) Option 3', 'd) Option 4'],
          cloMapping: 'CLO1:PLO1 - 2 marks'
        };
      } else {
        newSubQuestion = {
          text: 'New sub-question',
          responseLines: 3,
          cloMapping: 'CLO1:PLO1 - 2 marks'
        };
      }
      
      subQuestions.push(newSubQuestion);
      subsections[subsectionIndex].questions[questionIndex].subQuestions = subQuestions;
      handleUpdateContent(sectionIndex, 'subsections', subsections);
    }
  };

  // Delete sub-question
  const handleDeleteSubQuestion = (sectionIndex, subsectionIndex, questionIndex, subQuestionIndex) => {
    const section = templateData.sections[sectionIndex];
    if (section.type === 'examSection') {
      const subsections = [...section.content.subsections];
      const question = subsections[subsectionIndex].questions[questionIndex];
      const subQuestions = [...question.subQuestions];
      
      subQuestions.splice(subQuestionIndex, 1);
      subsections[subsectionIndex].questions[questionIndex].subQuestions = subQuestions;
      handleUpdateContent(sectionIndex, 'subsections', subsections);
    }
  };

  // Generate HTML from template data
  const generateHTML = () => {
    // Basic HTML structure
    let html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${templateData.name}</title>
  <style>
    body {
      font-family: ${templateData.settings.font.family};
      font-size: ${templateData.settings.font.size};
      line-height: ${templateData.settings.font.lineHeight};
      margin: ${templateData.settings.margins.top} ${templateData.settings.margins.right} ${templateData.settings.margins.bottom} ${templateData.settings.margins.left};
      background: white;
      color: black;
    }
    
    @page {
      size: ${templateData.settings.pageSize} ${templateData.settings.orientation};
    }
    
    @media print {
      body {
        margin: 0;
        padding: ${templateData.settings.margins.top} ${templateData.settings.margins.right} ${templateData.settings.margins.bottom} ${templateData.settings.margins.left};
      }
      .page-break {
        page-break-before: always;
        break-before: page;
      }
    }
`;

    // Add template-specific styles
    if (templateType === 'cover') {
      html += `
    .cover-box {
      border: 2px solid black;
      padding: 2em;
      max-width: 800px;
      margin: auto;
    }
    
    .logo-container {
      text-align: center;
      margin-bottom: 1em;
    }
    
    .logo {
      width: 300px;
      height: auto;
    }
    
    .center-text {
      text-align: center;
    }
    
    .left-text {
      text-align: left;
    }
    
    .institution p, .exam-title p, .footer-note {
      margin: 0.5em 0;
    }
    
    .course-info, .instructions {
      margin-top: 2em;
    }
    
    .divider-line, .note-line {
      border-top: 2px solid black;
      margin: 1em 0;
    }
    
    .indented {
      padding-left: 2pc;
    }
    
    .label {
      display: inline-block;
      width: 160px;
    }
    
    .value {
      display: inline-block;
    }`;
    } else {
      // Body template styles
      html += `
    .page-container {
      width: calc(8.27in - ${templateData.settings.margins.left} - ${templateData.settings.margins.right});
      max-width: 100%;
      margin: 0 auto;
      position: relative;
    }
    
    .exam-header {
      text-align: right;
      font-size: 12pt;
      font-weight: bold;
      margin-bottom: 20px;
    }
    
    .clo-table-title {
      text-align: center;
      font-weight: bold;
      font-size: 12pt;
      margin: 20px 0 5px 0;
    }
    
    .clo-table {
      width: 100%;
      border-collapse: collapse;
      margin: 15px 0 20px 0;
      font-size: 11pt;
    }
    
    .clo-table td {
      border: 1px solid black;
      padding: 6px 8px;
      vertical-align: top;
    }
    
    .clo-label, .plo-label {
      font-weight: bold;
      width: 10%;
    }
    
    .description {
      font-weight: normal;
      width: 80%;
    }
    
    .section-title {
      font-size: 13pt;
      font-weight: bold;
      margin: 25px 0 15px 0;
      text-align: left;
    }
    
    .instruction {
      margin: 12px 0;
      font-weight: normal;
    }
    
    .section-a-list {
      list-style-type: lower-alpha;
      margin-left: 20px;
      margin-top: 10px;
    }
    
    .section-a-list li {
      margin: 10px 0;
    }
    
    .roman-list {
      list-style-type: lower-roman;
      margin-left: 30px;
      margin-top: 6px;
    }
    
    .roman-list li {
      margin: 6px 0;
    }
    
    .numbered-list {
      list-style-type: decimal;
      margin-left: 20px;
      margin-top: 10px;
    }
    
    .numbered-list li {
      margin: 10px 0;
    }
    
    .fill-line {
      display: inline-block;
      min-width: 120px;
      border-bottom: 1px solid black;
      margin: 0 4px;
      vertical-align: bottom;
    }
    
    .response-area {
      width: 100%;
      min-height: 60px;
      background-image: repeating-linear-gradient(
        transparent 0px,
        transparent 18px,
        black 18px,
        black 18px
      );
      background-position: left bottom;
      background-size: 100% 18px;
      margin: 10px 0;
    }
    
    .clo-marker {
      text-align: right;
      font-weight: bold;
      font-size: 10pt;
      margin: 15px 0 5px;
    }
    
    .footer {
      display: flex;
      justify-content: space-between;
      font-size: 10pt;
      width: 100%;
      margin-top: 30px;
    }`;

      // Add page numbering if needed
      if (templateData.settings.pageNumbering) {
        html += `
    body {
      counter-reset: page-number ${templateData.settings.pageNumbering.start - 1};
    }
    
    .page-counter {
      counter-increment: page-number;
      height: 1px;
    }
    
    .footer-center::before {
      content: counter(page-number) " of 10";
    }`;
      }
    }

    // Close style tag
    html += `
  </style>
</head>
<body>`;

    // Generate body content based on template type
    if (templateType === 'cover') {
      html += generateCoverHTML();
    } else {
      html += generateBodyHTML();
    }

    // Close body and HTML tags
    html += `
</body>
</html>`;

    setGeneratedHTML(html);
    return html;
  };

  // Generate cover page HTML
  const generateCoverHTML = () => {
    let html = `
  <div class="cover-box">`;

    // Generate each section
    templateData.sections.forEach(section => {
      switch (section.type) {
        case 'header':
          html += `
    <div class="logo-container">
      <img src="${section.content.logo}" alt="University Logo" class="logo">
    </div>
    <div class="institution center-text">
      <p><strong>${section.content.institution}</strong></p>
      <p><strong>${section.content.location}</strong></p>
      <p><strong>${section.content.address}</strong></p>
      ${section.style.divider ? '<div class="divider-line"></div>' : ''}
    </div>`;
          break;
        case 'title':
          html += `
    <div class="exam-title center-text">
      <p><strong>${section.content.examType}</strong></p>
      <p><strong>${section.content.programLevel}</strong></p>
      <p><strong>${section.content.semesterSession}</strong></p>
    </div>`;
          break;
        case 'courseInfo':
          html += `
    <div class="course-info left-text ${section.style.indented ? 'indented' : ''}">
      <p><span class="label">COURSE</span> : <span class="value">${section.content.courseTitle}</span></p>
      <p><span class="label">COURSE CODE</span> : <span class="value">${section.content.courseCode}</span></p>
      <p><span class="label">DURATION</span> : <span class="value">${section.content.duration}</span></p>
      <p><span class="label">PROGRAMME CODE</span> : <span class="value">${section.content.programmeCode}</span></p>
    </div>`;
          break;
        case 'instructions':
          html += `
    <div class="instructions left-text ${section.style.indented ? 'indented' : ''}">
      <p><strong>${section.content.title}</strong></p>`;
          
          section.content.items.forEach(instruction => {
            html += `      <p>${instruction}</p>`;
          });
          
          html += `
    </div>`;
          break;
        case 'footer':
          html += `
    <div class="note-line"></div>
    <p class="footer-note center-text">
      THIS QUESTION PAPER CONTAINS ${section.content.pageCount} PAGES INCLUSIVE OF THIS PAGE.
    </p>`;
          break;
      }
    });

    html += `
  </div>`;

    return html;
  };

  // Generate body page HTML
  const generateBodyHTML = () => {
    let html = `
  <div class="page-container">`;

    // Generate each section
    templateData.sections.forEach(section => {
      switch (section.type) {
        case 'pageHeader':
          html += `
    <div class="exam-header" style="text-align: ${section.style.alignment}; font-weight: ${section.style.bold ? 'bold' : 'normal'}">
      (${section.content.confidential})
    </div>`;
          break;
        case 'cloTable':
          html += `
    <div class="clo-table-title">${section.content.title}</div>
    <table class="clo-table">`;
          
          section.content.clos.forEach(clo => {
            html += `
      <tr>
        <td class="clo-label">${clo.number}</td>
        <td class="description">${clo.description}</td>
        <td class="plo-label">${clo.plo}</td>
      </tr>`;
          });
          
          html += `
    </table>`;
          break;
        case 'examSection':
          html += `
    <div class="section-title">${section.content.title}</div>
    <div class="instruction">${section.content.instruction}</div>`;
          
          // Generate subsections
          section.content.subsections.forEach(subsection => {
            html += `
    <div class="instruction">${subsection.title}</div>`;
            
            if (subsection.instruction) {
              html += `
    <div class="instruction">${subsection.instruction}</div>`;
            }
            
            // Generate questions
            subsection.questions.forEach(question => {
              if (question.type === 'multiple-choice') {
                html += `
    <ol class="section-a-list">
      <li>${question.text}
        <ol class="roman-list">`;
                
                // Generate sub-questions
                question.subQuestions.forEach(subQ => {
                  html += `
          <li>${subQ.text}
            <ul style="list-style-type: lower-alpha; margin-left: 30px; margin-top: 6px;">`;
                  
                  // Generate options
                  subQ.options.forEach(option => {
                    html += `
              <li>${option}</li>`;
                  });
                  
                  html += `
            </ul>
            <div class="clo-marker">(${subQ.cloMapping})</div>
          </li>`;
                });
                
                html += `
        </ol>
      </li>
    </ol>`;
              } else if (question.type === 'short-answer') {
                html += `
    <ol class="numbered-list">`;
                
                // Generate sub-questions
                question.subQuestions.forEach(subQ => {
                  html += `
      <li>${subQ.text}
        <div class="response-area"></div>
        <div class="clo-marker">(${subQ.cloMapping})</div>
      </li>`;
                });
                
                html += `
    </ol>`;
              } else if (question.type === 'essay') {
                // Generate essay questions
                question.subQuestions.forEach(subQ => {
                  html += `
    <p>${subQ.text}</p>
    <div class="response-area" style="min-height: ${subQ.responseLines * 18}px;"></div>
    <div class="clo-marker">(${subQ.cloMapping})</div>`;
                });
              }
            });
          });
          break;
        case 'pageFooter':
          html += `
    <div class="footer">
      <div class="footer-left">${section.content.left}</div>
      <div class="footer-center"></div>
      <div class="footer-right">${section.content.right}</div>
    </div>
    <div class="page-counter"></div>`;
          break;
      }
    });

    html += `
  </div>`;

    return html;
  };

  // Save template
  const saveTemplate = async () => {
    setIsGenerating(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate processing
      
      const html = generateHTML();
      
      const templateObj = {
        ...templateData,
        htmlContent: html,
        finalHTML: html,
        updatedAt: new Date().toISOString()
      };
      
      const templates = JSON.parse(localStorage.getItem('exam_templates') || '[]');
      const existingIndex = templates.findIndex(t => t.id === templateObj.id);
      
      if (existingIndex >= 0) {
        templates[existingIndex] = templateObj;
      } else {
        templates.push(templateObj);
      }
      
      localStorage.setItem('exam_templates', JSON.stringify(templates));
      alert('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Download HTML
  const downloadHTML = () => {
    const html = generateHTML();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${templateData.name.replace(/\s+/g, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // View as PDF
  const viewAsPDF = () => {
    try {
      const html = generateHTML();
      
      // Create a new window with the HTML content
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        alert("Please allow pop-ups to view the PDF");
        return;
      }
      
      // Write content to the new window with proper doctype
      printWindow.document.write('<!DOCTYPE html>');
      printWindow.document.write(html);
      
      // Ensure the document is fully loaded before printing
      printWindow.document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      });
      
      // Close only happens after print dialog is closed
      printWindow.document.close();
    } catch (error) {
      console.error("Error generating PDF view:", error);
      alert("There was an error generating the PDF view. Please try again.");
    }
  };

  // Render section editor based on section type
  const renderSectionEditor = () => {
    if (selectedSectionIndex === null) return null;
    
    const section = templateData.sections[selectedSectionIndex];
    
    return (
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-800">
            Edit {section.type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </h3>
          <button
            onClick={() => handleDeleteSection(selectedSectionIndex)}
            className="p-2 text-red-600 hover:bg-red-50 rounded"
            title="Delete Section"
          >
            <SafeIcon icon={FiTrash2} />
          </button>
        </div>
        
        {/* Render different editors based on section type */}
        {section.type === 'header' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
              <input
                type="text"
                value={section.content.logo}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'logo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Institution Name</label>
              <input
                type="text"
                value={section.content.institution}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'institution', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={section.content.location}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'location', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={section.content.address}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={section.style.divider}
                onChange={(e) => handleUpdateStyle(selectedSectionIndex, 'divider', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">Show divider line</label>
            </div>
          </div>
        )}
        
        {section.type === 'title' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exam Type</label>
              <input
                type="text"
                value={section.content.examType}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'examType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Program Level</label>
              <input
                type="text"
                value={section.content.programLevel}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'programLevel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Semester Session</label>
              <input
                type="text"
                value={section.content.semesterSession}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'semesterSession', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
        
        {section.type === 'courseInfo' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Title</label>
              <input
                type="text"
                value={section.content.courseTitle}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'courseTitle', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Code</label>
              <input
                type="text"
                value={section.content.courseCode}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'courseCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <input
                type="text"
                value={section.content.duration}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'duration', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Programme Code</label>
              <input
                type="text"
                value={section.content.programmeCode}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'programmeCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={section.style.indented}
                onChange={(e) => handleUpdateStyle(selectedSectionIndex, 'indented', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">Indented</label>
            </div>
          </div>
        )}
        
        {section.type === 'instructions' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={section.content.title}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
              <div className="space-y-2">
                {section.content.items.map((item, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => {
                        const items = [...section.content.items];
                        items[index] = e.target.value;
                        handleUpdateContent(selectedSectionIndex, 'items', items);
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => handleDeleteInstructionItem(selectedSectionIndex, index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => handleAddInstructionItem(selectedSectionIndex)}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <SafeIcon icon={FiPlus} className="mr-1" />
                  Add Instruction
                </button>
              </div>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={section.style.indented}
                onChange={(e) => handleUpdateStyle(selectedSectionIndex, 'indented', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">Indented</label>
            </div>
          </div>
        )}
        
        {section.type === 'footer' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Page Count Text</label>
              <input
                type="text"
                value={section.content.pageCount}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'pageCount', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={section.style.divider}
                onChange={(e) => handleUpdateStyle(selectedSectionIndex, 'divider', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">Show divider line</label>
            </div>
          </div>
        )}
        
        {section.type === 'pageHeader' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Header Text</label>
              <input
                type="text"
                value={section.content.confidential}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'confidential', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Alignment</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleUpdateStyle(selectedSectionIndex, 'alignment', 'left')}
                  className={`p-2 border border-gray-300 rounded ${
                    section.style.alignment === 'left' ? 'bg-blue-500 text-white' : 'bg-white'
                  }`}
                >
                  <SafeIcon icon={FiAlignLeft} />
                </button>
                <button
                  onClick={() => handleUpdateStyle(selectedSectionIndex, 'alignment', 'center')}
                  className={`p-2 border border-gray-300 rounded ${
                    section.style.alignment === 'center' ? 'bg-blue-500 text-white' : 'bg-white'
                  }`}
                >
                  <SafeIcon icon={FiAlignCenter} />
                </button>
                <button
                  onClick={() => handleUpdateStyle(selectedSectionIndex, 'alignment', 'right')}
                  className={`p-2 border border-gray-300 rounded ${
                    section.style.alignment === 'right' ? 'bg-blue-500 text-white' : 'bg-white'
                  }`}
                >
                  <SafeIcon icon={FiAlignRight} />
                </button>
              </div>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={section.style.bold}
                onChange={(e) => handleUpdateStyle(selectedSectionIndex, 'bold', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">Bold</label>
            </div>
          </div>
        )}
        
        {section.type === 'cloTable' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Table Title</label>
              <input
                type="text"
                value={section.content.title}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">CLOs</label>
                <button
                  onClick={() => handleAddCLO(selectedSectionIndex)}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <SafeIcon icon={FiPlus} className="mr-1" />
                  Add CLO
                </button>
              </div>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {section.content.clos.map((clo, cloIndex) => (
                  <div key={cloIndex} className="p-2 border border-gray-200 rounded">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={clo.number}
                          onChange={(e) => {
                            const clos = [...section.content.clos];
                            clos[cloIndex].number = e.target.value;
                            handleUpdateContent(selectedSectionIndex, 'clos', clos);
                          }}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="CLO#"
                        />
                        <select
                          value={clo.plo}
                          onChange={(e) => {
                            const clos = [...section.content.clos];
                            clos[cloIndex].plo = e.target.value;
                            handleUpdateContent(selectedSectionIndex, 'clos', clos);
                          }}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          <option value="PLO1">PLO1</option>
                          <option value="PLO2">PLO2</option>
                          <option value="PLO3">PLO3</option>
                          <option value="PLO4">PLO4</option>
                          <option value="PLO5">PLO5</option>
                        </select>
                      </div>
                      <button
                        onClick={() => handleDeleteCLO(selectedSectionIndex, cloIndex)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <SafeIcon icon={FiX} className="text-sm" />
                      </button>
                    </div>
                    <textarea
                      value={clo.description}
                      onChange={(e) => {
                        const clos = [...section.content.clos];
                        clos[cloIndex].description = e.target.value;
                        handleUpdateContent(selectedSectionIndex, 'clos', clos);
                      }}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      rows={2}
                      placeholder="CLO description"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {section.type === 'examSection' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
              <input
                type="text"
                value={section.content.title}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instruction</label>
              <input
                type="text"
                value={section.content.instruction}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'instruction', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">Subsections</label>
                <button
                  onClick={() => handleAddSubsection(selectedSectionIndex)}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <SafeIcon icon={FiPlus} className="mr-1" />
                  Add Subsection
                </button>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {section.content.subsections.map((subsection, subsectionIndex) => (
                  <div key={subsectionIndex} className="p-2 border border-gray-200 rounded">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-sm">Subsection {subsectionIndex + 1}</h4>
                      <button
                        onClick={() => handleDeleteSubsection(selectedSectionIndex, subsectionIndex)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <SafeIcon icon={FiX} className="text-sm" />
                      </button>
                    </div>
                    <div className="space-y-2 mb-3">
                      <input
                        type="text"
                        value={subsection.title}
                        onChange={(e) => {
                          const subsections = [...section.content.subsections];
                          subsections[subsectionIndex].title = e.target.value;
                          handleUpdateContent(selectedSectionIndex, 'subsections', subsections);
                        }}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="Subsection title"
                      />
                      {subsection.instruction && (
                        <input
                          type="text"
                          value={subsection.instruction}
                          onChange={(e) => {
                            const subsections = [...section.content.subsections];
                            subsections[subsectionIndex].instruction = e.target.value;
                            handleUpdateContent(selectedSectionIndex, 'subsections', subsections);
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Instruction"
                        />
                      )}
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="text-xs font-medium text-gray-600">Questions</h5>
                      <button
                        onClick={() => handleAddQuestion(selectedSectionIndex, subsectionIndex)}
                        className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        <SafeIcon icon={FiPlus} className="mr-1 text-xs" />
                        Add Question
                      </button>
                    </div>
                    <div className="pl-2 space-y-3">
                      {subsection.questions.map((question, questionIndex) => (
                        <div key={questionIndex} className="p-2 border border-gray-100 rounded bg-gray-50">
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs font-medium">Q{questionIndex + 1}</span>
                              <select
                                value={question.type}
                                onChange={(e) => {
                                  const subsections = [...section.content.subsections];
                                  subsections[subsectionIndex].questions[questionIndex].type = e.target.value;
                                  handleUpdateContent(selectedSectionIndex, 'subsections', subsections);
                                }}
                                className="px-1 py-0.5 text-xs border border-gray-300 rounded"
                              >
                                <option value="multiple-choice">Multiple Choice</option>
                                <option value="short-answer">Short Answer</option>
                                <option value="essay">Essay</option>
                                <option value="fill-blank">Fill in Blank</option>
                              </select>
                            </div>
                            <button
                              onClick={() => handleDeleteQuestion(selectedSectionIndex, subsectionIndex, questionIndex)}
                              className="p-0.5 text-red-500 hover:bg-red-50 rounded"
                            >
                              <SafeIcon icon={FiX} className="text-xs" />
                            </button>
                          </div>
                          <input
                            type="text"
                            value={question.text}
                            onChange={(e) => {
                              const subsections = [...section.content.subsections];
                              subsections[subsectionIndex].questions[questionIndex].text = e.target.value;
                              handleUpdateContent(selectedSectionIndex, 'subsections', subsections);
                            }}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs mb-2"
                            placeholder="Question text"
                          />
                          <div className="pl-2 space-y-2">
                            {question.subQuestions.map((subQ, subQIndex) => (
                              <div key={subQIndex} className="p-1 border border-gray-100 rounded bg-white">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-xs">Subquestion {subQIndex + 1}</span>
                                  <button
                                    onClick={() => handleDeleteSubQuestion(selectedSectionIndex, subsectionIndex, questionIndex, subQIndex)}
                                    className="p-0.5 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <SafeIcon icon={FiX} className="text-xs" />
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={subQ.text}
                                  onChange={(e) => {
                                    const subsections = [...section.content.subsections];
                                    subsections[subsectionIndex].questions[questionIndex].subQuestions[subQIndex].text = e.target.value;
                                    handleUpdateContent(selectedSectionIndex, 'subsections', subsections);
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs mb-1"
                                  placeholder="Subquestion text"
                                />
                                {question.type === 'multiple-choice' && (
                                  <div className="space-y-1 mb-1">
                                    {subQ.options.map((option, optIndex) => (
                                      <input
                                        key={optIndex}
                                        type="text"
                                        value={option}
                                        onChange={(e) => {
                                          const subsections = [...section.content.subsections];
                                          subsections[subsectionIndex].questions[questionIndex].subQuestions[subQIndex].options[optIndex] = e.target.value;
                                          handleUpdateContent(selectedSectionIndex, 'subsections', subsections);
                                        }}
                                        className="w-full px-2 py-0.5 border border-gray-300 rounded text-xs"
                                        placeholder={`Option ${optIndex + 1}`}
                                      />
                                    ))}
                                  </div>
                                )}
                                {(question.type === 'short-answer' || question.type === 'essay') && (
                                  <div className="flex items-center space-x-2 mb-1">
                                    <label className="text-xs">Lines:</label>
                                    <input
                                      type="number"
                                      min="1"
                                      max="50"
                                      value={subQ.responseLines || 3}
                                      onChange={(e) => {
                                        const subsections = [...section.content.subsections];
                                        subsections[subsectionIndex].questions[questionIndex].subQuestions[subQIndex].responseLines = parseInt(e.target.value);
                                        handleUpdateContent(selectedSectionIndex, 'subsections', subsections);
                                      }}
                                      className="w-16 px-2 py-0.5 border border-gray-300 rounded text-xs"
                                    />
                                  </div>
                                )}
                                <input
                                  type="text"
                                  value={subQ.cloMapping}
                                  onChange={(e) => {
                                    const subsections = [...section.content.subsections];
                                    subsections[subsectionIndex].questions[questionIndex].subQuestions[subQIndex].cloMapping = e.target.value;
                                    handleUpdateContent(selectedSectionIndex, 'subsections', subsections);
                                  }}
                                  className="w-full px-2 py-0.5 border border-gray-300 rounded text-xs"
                                  placeholder="CLO:PLO - marks"
                                />
                              </div>
                            ))}
                            <button
                              onClick={() => handleAddSubQuestion(selectedSectionIndex, subsectionIndex, questionIndex)}
                              className="w-full px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded hover:bg-gray-300"
                            >
                              <SafeIcon icon={FiPlus} className="mr-1 text-xs" />
                              Add Subquestion
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {section.type === 'pageFooter' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Left Footer</label>
              <input
                type="text"
                value={section.content.left}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'left', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Center Footer</label>
              <input
                type="text"
                value={section.content.center}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'center', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="{page} of {total}"
              />
              <p className="text-xs text-gray-500 mt-1">Use {'{page}'} and {'{total}'} as placeholders</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Right Footer</label>
              <input
                type="text"
                value={section.content.right}
                onChange={(e) => handleUpdateContent(selectedSectionIndex, 'right', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="ml-72 min-h-screen flex flex-col">
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <motion.button
              onClick={onBack}
              className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Back to Templates</span>
            </motion.button>

            <div className="h-6 w-px bg-gray-300" />

            <SafeIcon icon={FiGrid} className="text-blue-600" />
            <input
              type="text"
              value={templateData.name}
              onChange={(e) => setTemplateData({ ...templateData, name: e.target.value })}
              className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2"
            />

            {/* Template Type Selector */}
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setTemplateType('cover')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  templateType === 'cover'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <SafeIcon icon={FiFileText} className="inline mr-1" />
                Cover Page
              </button>
              <button
                onClick={() => setTemplateType('body')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  templateType === 'body'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <SafeIcon icon={FiLayers} className="inline mr-1" />
                Body Page
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowPreview(true)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiEye} />
              <span>Preview</span>
            </button>
            <button
              onClick={viewAsPDF}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiPrinter} />
              <span>PDF</span>
            </button>
            <button
              onClick={downloadHTML}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiDownload} />
              <span>HTML</span>
            </button>
            <motion.button
              onClick={saveTemplate}
              disabled={isGenerating}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
              whileHover={{ scale: isGenerating ? 1 : 1.02 }}
              whileTap={{ scale: isGenerating ? 1 : 0.98 }}
            >
              {isGenerating ? (
                <>
                  <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} />
                  <span>Save Template</span>
                </>
              )}
            </motion.button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex">
          {/* Left Panel - Components and Settings */}
          <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setShowComponentPanel(true)}
                className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  showComponentPanel
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiGrid} className="inline mr-1" />
                Components
              </button>
              <button
                onClick={() => setShowComponentPanel(false)}
                className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  !showComponentPanel
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiSettings} className="inline mr-1" />
                Settings
              </button>
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-auto">
              {showComponentPanel ? (
                <div className="p-4 space-y-4">
                  <h3 className="font-medium text-gray-800">Available Components</h3>
                  <div className="space-y-2">
                    {(templateType === 'cover' ? COVER_COMPONENTS : BODY_COMPONENTS).map((component) => (
                      <div
                        key={component.id}
                        className="p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-move hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        draggable
                        onDragStart={(e) => handleDragStart(e, component, 'new')}
                      >
                        <div className="flex items-center">
                          <SafeIcon icon={component.icon} className="text-blue-600 mr-2" />
                          <span className="text-sm">{component.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  <h3 className="font-medium text-gray-800">Template Settings</h3>
                  
                  {/* Page Settings */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSettingsSection('page')}
                      className="w-full flex justify-between items-center p-3 bg-gray-50 text-left"
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiLayout} className="text-blue-600 mr-2" />
                        <span className="text-sm font-medium">Page Settings</span>
                      </div>
                      <SafeIcon
                        icon={expandedSettings.page ? FiChevronDown : FiChevronRight}
                        className="text-gray-500"
                      />
                    </button>
                    
                    {expandedSettings.page && (
                      <div className="p-3 border-t border-gray-200 space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Page Size</label>
                          <select
                            value={templateData.settings.pageSize}
                            onChange={(e) => handleUpdateSettings('pageSize', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          >
                            <option value="A4">A4</option>
                            <option value="Letter">Letter</option>
                            <option value="Legal">Legal</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Orientation</label>
                          <select
                            value={templateData.settings.orientation}
                            onChange={(e) => handleUpdateSettings('orientation', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          >
                            <option value="portrait">Portrait</option>
                            <option value="landscape">Landscape</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Margins</label>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-0.5">Top</label>
                              <input
                                type="text"
                                value={templateData.settings.margins.top}
                                onChange={(e) => handleUpdateNestedSettings('margins', 'top', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-0.5">Right</label>
                              <input
                                type="text"
                                value={templateData.settings.margins.right}
                                onChange={(e) => handleUpdateNestedSettings('margins', 'right', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-0.5">Bottom</label>
                              <input
                                type="text"
                                value={templateData.settings.margins.bottom}
                                onChange={(e) => handleUpdateNestedSettings('margins', 'bottom', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-0.5">Left</label>
                              <input
                                type="text"
                                value={templateData.settings.margins.left}
                                onChange={(e) => handleUpdateNestedSettings('margins', 'left', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Font Settings */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSettingsSection('font')}
                      className="w-full flex justify-between items-center p-3 bg-gray-50 text-left"
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiType} className="text-blue-600 mr-2" />
                        <span className="text-sm font-medium">Font Settings</span>
                      </div>
                      <SafeIcon
                        icon={expandedSettings.font ? FiChevronDown : FiChevronRight}
                        className="text-gray-500"
                      />
                    </button>
                    
                    {expandedSettings.font && (
                      <div className="p-3 border-t border-gray-200 space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Font Family</label>
                          <select
                            value={templateData.settings.font.family}
                            onChange={(e) => handleUpdateNestedSettings('font', 'family', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          >
                            <option value="Arial, sans-serif">Arial</option>
                            <option value="'Times New Roman', serif">Times New Roman</option>
                            <option value="Calibri, sans-serif">Calibri</option>
                            <option value="Cambria, serif">Cambria</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Font Size</label>
                          <select
                            value={templateData.settings.font.size}
                            onChange={(e) => handleUpdateNestedSettings('font', 'size', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          >
                            <option value="10pt">10pt</option>
                            <option value="11pt">11pt</option>
                            <option value="12pt">12pt</option>
                            <option value="14pt">14pt</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Line Height</label>
                          <select
                            value={templateData.settings.font.lineHeight}
                            onChange={(e) => handleUpdateNestedSettings('font', 'lineHeight', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          >
                            <option value="1">Single (1)</option>
                            <option value="1.15">Narrow (1.15)</option>
                            <option value="1.5">1.5 lines</option>
                            <option value="2">Double (2)</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Page Numbering (Body template only) */}
                  {templateType === 'body' && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSettingsSection('numbering')}
                        className="w-full flex justify-between items-center p-3 bg-gray-50 text-left"
                      >
                        <div className="flex items-center">
                          <SafeIcon icon={FiFileText} className="text-blue-600 mr-2" />
                          <span className="text-sm font-medium">Page Numbering</span>
                        </div>
                        <SafeIcon
                          icon={expandedSettings.numbering ? FiChevronDown : FiChevronRight}
                          className="text-gray-500"
                        />
                      </button>
                      
                      {expandedSettings.numbering && (
                        <div className="p-3 border-t border-gray-200 space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Start From Page</label>
                            <input
                              type="number"
                              min="1"
                              value={templateData.settings.pageNumbering?.start || 1}
                              onChange={(e) => {
                                const pageNumbering = templateData.settings.pageNumbering || {};
                                handleUpdateSettings('pageNumbering', {
                                  ...pageNumbering,
                                  start: parseInt(e.target.value)
                                });
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Format</label>
                            <input
                              type="text"
                              value={templateData.settings.pageNumbering?.format || '{page} of {total}'}
                              onChange={(e) => {
                                const pageNumbering = templateData.settings.pageNumbering || {};
                                handleUpdateSettings('pageNumbering', {
                                  ...pageNumbering,
                                  format: e.target.value
                                });
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              placeholder="{page} of {total}"
                            />
                            <p className="text-xs text-gray-500 mt-1">Use {'{page}'} and {'{total}'} as placeholders</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Middle Panel - Builder Canvas */}
          <div className="flex-1 flex flex-col bg-gray-100 overflow-auto">
            <div className="p-4">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="border-2 border-dashed border-gray-300 rounded-lg min-h-[80vh] p-4">
                  {templateData.sections.length === 0 ? (
                    <div className="flex items-center justify-center h-64 text-gray-500">
                      <div className="text-center">
                        <SafeIcon icon={FiGrid} className="text-4xl mb-2 mx-auto" />
                        <p className="mb-2">Drag components here to build your template</p>
                        <p className="text-sm">Or choose a component from the left panel</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {templateData.sections.map((section, index) => (
                        <div
                          key={section.id}
                          className={`relative border rounded-lg ${
                            selectedSectionIndex === index
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 bg-white'
                          } ${
                            dragOverIndex === index ? 'border-green-500 bg-green-50' : ''
                          }`}
                          onClick={() => handleSelectSection(index)}
                          draggable
                          onDragStart={(e) => handleDragStart(e, { index, id: section.id }, 'existing')}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDrop={(e) => handleDrop(e, index)}
                        >
                          <div className="flex items-center justify-between p-3">
                            <div className="flex items-center">
                              <SafeIcon icon={FiMove} className="text-gray-400 mr-2 cursor-move" />
                              <span className="font-medium text-gray-700">
                                {section.type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <button
                                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                                title="Delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSection(index);
                                }}
                              >
                                <SafeIcon icon={FiTrash2} className="text-sm" />
                              </button>
                            </div>
                          </div>

                          <div className="px-3 pb-3">
                            {section.type === 'header' && (
                              <div className="text-center p-2 bg-gray-50 rounded">
                                <div className="text-xs text-gray-500 mb-1">Institution Header</div>
                                <div className="font-medium">{section.content.institution}</div>
                                <div className="text-sm">{section.content.location}</div>
                                <div className="text-sm">{section.content.address}</div>
                              </div>
                            )}
                            
                            {section.type === 'title' && (
                              <div className="text-center p-2 bg-gray-50 rounded">
                                <div className="text-xs text-gray-500 mb-1">Exam Title</div>
                                <div className="font-medium">{section.content.examType}</div>
                                <div className="text-sm">{section.content.programLevel}</div>
                                <div className="text-sm">{section.content.semesterSession}</div>
                              </div>
                            )}
                            
                            {section.type === 'courseInfo' && (
                              <div className="p-2 bg-gray-50 rounded">
                                <div className="text-xs text-gray-500 mb-1">Course Info</div>
                                <div className="grid grid-cols-2 gap-1 text-sm">
                                  <div>Course:</div>
                                  <div>{section.content.courseTitle}</div>
                                  <div>Code:</div>
                                  <div>{section.content.courseCode}</div>
                                  <div>Duration:</div>
                                  <div>{section.content.duration}</div>
                                </div>
                              </div>
                            )}
                            
                            {section.type === 'instructions' && (
                              <div className="p-2 bg-gray-50 rounded">
                                <div className="text-xs text-gray-500 mb-1">Instructions</div>
                                <div className="font-medium">{section.content.title}</div>
                                <ul className="list-disc pl-5 text-sm">
                                  {section.content.items.map((item, i) => (
                                    <li key={i}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {section.type === 'footer' && (
                              <div className="text-center p-2 bg-gray-50 rounded">
                                <div className="text-xs text-gray-500 mb-1">Footer Note</div>
                                <div className="text-sm">THIS QUESTION PAPER CONTAINS {section.content.pageCount} PAGES INCLUSIVE OF THIS PAGE.</div>
                              </div>
                            )}
                            
                            {section.type === 'pageHeader' && (
                              <div className={`p-2 bg-gray-50 rounded text-${section.style.alignment}`}>
                                <div className="text-xs text-gray-500 mb-1">Page Header</div>
                                <div className={section.style.bold ? 'font-bold' : ''}>({section.content.confidential})</div>
                              </div>
                            )}
                            
                            {section.type === 'cloTable' && (
                              <div className="p-2 bg-gray-50 rounded">
                                <div className="text-xs text-gray-500 mb-1">CLO Table</div>
                                <div className="font-medium text-center mb-1">{section.content.title}</div>
                                <table className="w-full text-xs border border-gray-300">
                                  <tbody>
                                    {section.content.clos.map((clo, i) => (
                                      <tr key={i} className="border-b border-gray-300">
                                        <td className="border-r border-gray-300 p-1 font-medium">{clo.number}</td>
                                        <td className="border-r border-gray-300 p-1">{clo.description}</td>
                                        <td className="p-1 font-medium">{clo.plo}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            
                            {section.type === 'examSection' && (
                              <div className="p-2 bg-gray-50 rounded">
                                <div className="text-xs text-gray-500 mb-1">Exam Section</div>
                                <div className="font-medium">{section.content.title}</div>
                                <div className="text-sm mb-1">{section.content.instruction}</div>
                                {section.content.subsections.map((subsection, i) => (
                                  <div key={i} className="mt-2 p-1 border border-gray-300 rounded bg-white">
                                    <div className="font-medium text-xs">{subsection.title}</div>
                                    <div className="text-xs text-gray-600">
                                      {subsection.questions.length} question(s)
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {section.type === 'pageFooter' && (
                              <div className="p-2 bg-gray-50 rounded">
                                <div className="text-xs text-gray-500 mb-1">Page Footer</div>
                                <div className="flex justify-between text-xs">
                                  <div>{section.content.left}</div>
                                  <div>{section.content.center}</div>
                                  <div>{section.content.right}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Drop area at the end */}
                      <div
                        className={`h-16 border-2 border-dashed rounded-lg flex items-center justify-center ${
                          dragOverIndex === templateData.sections.length ? 'border-green-500 bg-green-50' : 'border-gray-300'
                        }`}
                        onDragOver={(e) => handleDragOver(e, templateData.sections.length)}
                        onDrop={(e) => handleDrop(e, templateData.sections.length)}
                      >
                        <span className="text-sm text-gray-500">Drop component here</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Section Editor */}
          <div className="w-64 bg-white border-l border-gray-200 flex flex-col overflow-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-medium text-gray-800">Properties</h3>
              <p className="text-sm text-gray-500">
                {selectedSectionIndex !== null ? 'Edit selected component' : 'Select a component to edit'}
              </p>
            </div>

            {renderSectionEditor()}

            {selectedSectionIndex === null && (
              <div className="p-4 text-center text-gray-500">
                <SafeIcon icon={FiEdit} className="text-2xl mb-2 mx-auto" />
                <p className="text-sm">Select a component from the canvas to edit its properties</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold">{templateData.name} Preview</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={viewAsPDF}
                  className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center space-x-2"
                >
                  <SafeIcon icon={FiPrinter} />
                  <span>Print/PDF</span>
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="p-8 overflow-auto max-h-[calc(90vh-120px)] bg-gray-100">
              <div
                className="bg-white shadow-2xl mx-auto"
                style={{ 
                  width: templateData.settings.pageSize === 'A4' ? '210mm' : '8.5in',
                  minHeight: templateData.settings.pageSize === 'A4' ? '297mm' : '11in'
                }}
              >
                <div 
                  className="w-full h-full"
                  style={{ 
                    height: templateData.settings.pageSize === 'A4' ? '297mm' : '11in',
                    padding: '1rem',
                    overflow: 'auto'
                  }}
                  dangerouslySetInnerHTML={{ __html: generatedHTML }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateEditor;